import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword,
  updateUserAttributes,
} from "aws-amplify/auth";
import {
  LoginCredentials,
  RegistrationData,
  PasswordResetData,
  EmailVerificationData,
  ForcePasswordChangeData,
  CurrentUser,
  UserRole,
  UserAttributes,
  AuthError,
  AuthSession,
} from "./types";
import { getDeviceTelemetry } from "../utils/deviceTelemetry";
 
// import './config/amplify'; // Ensure Amplify is configured before using AuthService
/**
 * Authentication service for Masti Manch platform
 * Provides methods to interact with AWS Cognito via AWS Amplify
 */
export class AuthService {
  private static instance: AuthService;

  private constructor() {
    Amplify.configure({"aws_project_region":"ap-south-1","aws_cognito_identity_pool_id":"ap-south-1:aa981625-2a8b-4636-8c0f-80bbd2c77dae","aws_cognito_region":"ap-south-1","aws_user_pools_id":"ap-south-1_t7gH30k45","aws_user_pools_web_client_id":"43ucvpqkv587vogts8dmmkag9s","oauth":{},"aws_cognito_username_attributes":["EMAIL"],"aws_cognito_social_providers":[],"aws_cognito_signup_attributes":[],"aws_cognito_mfa_configuration":"OFF","aws_cognito_mfa_types":["SMS"],"aws_cognito_password_protection_settings":{"passwordPolicyMinLength":8,"passwordPolicyCharacters":["REQUIRES_LOWERCASE","REQUIRES_NUMBERS","REQUIRES_SYMBOLS","REQUIRES_UPPERCASE"]},"aws_cognito_verification_mechanisms":["EMAIL"],"aws_user_files_s3_bucket":"prod-mastimanch-content","aws_user_files_s3_bucket_region":"ap-south-1"} as any)
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }
    
  /**
   * Sign up a new user (Student or Guardian registration)
   */
  async signUp(data: RegistrationData): Promise<any> {
    try {
      const { email, password, attributes } = data;

      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            "custom:role": attributes.role,
            "custom:board": attributes.board || "",
            "custom:standard": attributes.standard || "",
            "custom:school": attributes.school || "",
            "custom:state": attributes.state || "",
            "custom:parentId": attributes.parentId || "",
            "custom:studentId": attributes.studentId || "",
            "custom:subscriptionStatus":
              attributes.subscriptionStatus || "trial",
            "custom:trialEndDate": attributes.trialEndDate || "",
            name: ((attributes.firstName || "") + " " + (attributes.lastName || "")).trim(),
            phone_number: attributes.phoneNumber || "",
            address: attributes.address || "",
          },
          autoSignIn: {
            enabled: true,
          },
        },
      });

      return result;
    } catch (error) {
      throw this.formatAuthError(error as any);
    }
  }

  /**
   * Confirm email verification code
   */
  async confirmSignUp(email: string, code: string): Promise<any> {
    try {
      const result = await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      return result;
    } catch (error) {
      throw this.formatAuthError(error as any);
    }
  }

  /**
   * Resend verification code
   */
  async resendSignUp(email: string): Promise<any> {
    try {
      const result = await resendSignUpCode({ username: email });
      return result;
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Sign in with email and password
   */
  async signIn(credentials: LoginCredentials): Promise<any> {
    try {
      const { email, password } = credentials;
      const user = await signIn({ username: email, password: password });

      // Sync device telemetry with backend for device limit enforcement and analytics
      try {
        const telemetry = getDeviceTelemetry();
        const token = await this.getAccessToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        await fetch('/api/auth/device-telemetry', {
          method: 'POST',
          headers,
          body: JSON.stringify({ email, deviceTelemetry: telemetry })
        });
      } catch (tErr) {
        console.warn('Could not sync device telemetry:', tErr);
      }

      // Check if new password is required (first login with temp password)
      if (
        user.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        return {
          user,
          requiresPasswordChange: true,
          session: user.isSignedIn,
        };
      }

      return {
        user,
        requiresPasswordChange: false,
        session: user.isSignedIn,
      };
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Complete new password requirement (force password change)
   */
  async completeNewPassword(user: any, newPassword: string): Promise<any> {
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });
      return result;
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut();
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<CurrentUser | null> {
    try {
      const cognitoUser = await getCurrentUser();

      if (!cognitoUser) {
        return null;
      }

      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const user: CurrentUser = {
        id: cognitoUser.userId || cognitoUser.username,
        email: attributes.email || cognitoUser.username,
        firstName: (attributes.name || "").split(" ")[0] || "",
        lastName: (attributes.name || "").split(" ").slice(1).join(" ") || "",
        role: (attributes["custom:role"] as UserRole) || UserRole.GUEST,
        attributes: Object.fromEntries(
          Object.entries(attributes).filter(([_, value]) => value !== undefined)
        ) as Record<string, string>,
        isEmailVerified: attributes.email_verified === "true",
        session: session
          ? {
              accessToken: session?.tokens?.accessToken?.toString() || "",
              idToken: session?.tokens?.idToken?.toString() || "",
              refreshToken: session.tokens?.accessToken?.toString() || "",
              expiresIn: session?.tokens?.accessToken?.payload?.exp || 0,
              tokenType:
                session?.tokens?.accessToken?.payload?.token_use?.toString() ||
                "",
            }
          : null,
      };

      return user;
    } catch (error: any) {
      // User is not authenticated
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current session (JWT tokens)
   */
  async getCurrentSession(): Promise<AuthSession | null> {
    try {
      const session = await fetchAuthSession();
      return {
        accessToken: session?.tokens?.accessToken?.toString() || "",
        idToken: session?.tokens?.idToken?.toString() || "",
        refreshToken: session.tokens?.accessToken?.toString() || "",
        expiresIn: session?.tokens?.accessToken?.payload?.exp || 0,
        tokenType:
          session?.tokens?.accessToken?.payload?.token_use?.toString() || "",
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get user's role from Cognito attributes
   */
  async getUserRole(): Promise<UserRole> {
    try {
      const user = await this.getCurrentUser();
      return user?.role || UserRole.GUEST;
    } catch (error) {
      return UserRole.GUEST;
    }
  }

  /**
   * Initiate forgot password flow
   */
  async forgotPassword(email: string): Promise<any> {
    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to initiate password reset.");
      }

      return await response.json();
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Confirm forgot password with verification code
   */
  async confirmForgotPassword(data: PasswordResetData): Promise<any> {
    try {
      const { email, code, newPassword } = data;
      const response = await fetch("/api/auth/confirm-forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reset password.");
      }

      return await response.json();
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<any> {
    try {
      const result = await updatePassword({ oldPassword, newPassword });
      return result;
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Update user attributes
   */
  async updateUserAttributes(
    attributes: Partial<UserAttributes>
  ): Promise<any> {
    try {
      const cognitoAttributes: Record<string, string> = {};
      if (attributes.firstName || attributes.lastName) {
        cognitoAttributes.name = ((attributes.firstName || "") + " " + (attributes.lastName || "")).trim();
      }
      if (attributes.role) cognitoAttributes["custom:role"] = attributes.role;
      if (attributes.board)
        cognitoAttributes["custom:board"] = attributes.board;
      if (attributes.standard)
        cognitoAttributes["custom:standard"] = attributes.standard;
      if (attributes.school)
        cognitoAttributes["custom:school"] = attributes.school;
      if (attributes.state)
        cognitoAttributes["custom:state"] = attributes.state;
      if (attributes.phoneNumber)
        cognitoAttributes.phone_number = attributes.phoneNumber;
      if (attributes.address) cognitoAttributes.address = attributes.address;

      const result = await updateUserAttributes({
        userAttributes: cognitoAttributes,
      });
      return result;
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Verify user attribute (e.g., email verification)
   */
  async verifyUserAttribute(attributeName: string, code: string): Promise<any> {
    try {
      const user = await getCurrentUser();
      await confirmUserAttribute({
        userAttributeKey: "email", // or .phoneNumber
        confirmationCode: code,
      });
    } catch (error: any) {
      throw this.formatAuthError(error);
    }
  }

  /**
   * Refresh JWT tokens
   */
  async refreshSession(): Promise<AuthSession | null> {
    try {
      const session = await fetchAuthSession();
      // Check if session needs refresh (expires in less than 5 minutes)
      const expiresIn = session?.tokens?.accessToken?.payload?.exp || 0;
      const currentTime = Math.floor(Date.now() / 1000);

      if (expiresIn - currentTime < 300) {
        // 5 minutes
        await fetchAuthSession({ forceRefresh: true });
      }

      return this.getCurrentSession();
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear all auth data (for debugging/testing)
   */
  async clearAuthData(): Promise<void> {
    try {
      await signOut({ global: true });
    } catch (error) {
      // Ignore errors
    }
  }

  /**
   * Format auth error for consistent error handling
   */
  private formatAuthError(error: any): AuthError {
    const authError: AuthError = {
      code: error.code || "UnknownError",
      message: error.message || "An unknown authentication error occurred",
      name: error.name || "AuthError",
    };

    // Map common Cognito error codes to user-friendly messages
    switch (error.code) {
      case "UserNotFoundException":
        authError.message = "User not found. Please check your email.";
        break;
      case "NotAuthorizedException":
        authError.message = "Incorrect email or password.";
        break;
      case "UserNotConfirmedException":
        authError.message = "Please verify your email before signing in.";
        break;
      case "CodeMismatchException":
        authError.message = "Invalid verification code. Please try again.";
        break;
      case "ExpiredCodeException":
        authError.message =
          "Verification code has expired. Please request a new one.";
        break;
      case "LimitExceededException":
        authError.message = "Too many attempts. Please try again later.";
        break;
      case "UsernameExistsException":
        authError.message = "An account with this email already exists.";
        break;
      case "InvalidParameterException":
        authError.message = "Invalid input. Please check your information.";
        break;
    }

    return authError;
  }

  /**
   * Convert Cognito user attributes array to map
   */
  private attributesToMap(
    attributes: Array<{ Name: string; Value: string }>
  ): Record<string, string> {
    return attributes.reduce((acc, attr) => {
      acc[attr.Name] = attr.Value;
      return acc;
    }, {} as Record<string, string>);
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();
